import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";

import { PageLayout } from "../../../../../../components/layouts/PageLayout";
import { TimePeriod } from "../../../../../../hooks/useDetectTimePeriod";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import {
  useFilterTimeQuery,
  useGetBilledHoursByCustomerIdQuery,
} from "../../../../../../slices/UserSlice";
import { GetPageTitle } from "../../../../../../support/ScrollToTop";
import { IContactProfile } from "../../../../../../types/types";
import { Time } from "../../../../../time/InvoiceTime";
import {
  createDefaultTimeFilter,
  createPresetClientFilter,
} from "../../../../../time/time-filter.utils";
import { TimeFilterKey } from "../../../../../time/TimeFilters";
import { TimesListView } from "../../../../../time/TimesListView";
import { TimeColumnKey } from "../../../../../time/types/time-list";

const TIME_PAGE_SIZE = 20;

const CONTACT_TIME_VISIBLE_FILTERS: TimeFilterKey[] = [
  TimeFilterKey.Date,
  TimeFilterKey.WorkType,
  TimeFilterKey.Assignee,
  TimeFilterKey.Status,
];

const CONTACT_TIME_VISIBLE_COLUMNS: TimeColumnKey[] = [
  TimeColumnKey.WorkName,
  TimeColumnKey.WorkType,
  TimeColumnKey.TotalTime,
  TimeColumnKey.Date,
  TimeColumnKey.Total,
  TimeColumnKey.Status,
  TimeColumnKey.Assignee,
  TimeColumnKey.Notes,
  TimeColumnKey.Type,
];

type Props = {
  contact: IContactProfile;
};

export const ContactTime: React.FC<Props> = ({ contact }) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const customerId = contact?.customerId || "";

  const presetFilters = useMemo<Partial<Time>>(
    () => ({
      filterByClient: customerId ? [createPresetClientFilter(customerId)] : [],
    }),
    [customerId],
  );

  const [filter, setFilter] = useState<Time>(
    createDefaultTimeFilter({
      orgId,
      presetFilters,
    }),
  );
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilter(
      createDefaultTimeFilter({
        orgId,
        presetFilters,
      }),
    );
  }, [orgId, presetFilters]);

  const timeQueryArgs = useMemo(
    () => ({
      filterByClient: customerId ? [customerId] : [],
      filterByAssignee: filter.filterByAssignee.map((item) => item.value),
      filterByWorkType: filter.filterByWorkType.map((item) => item.value),
      filterByStatus: filter.filterByStatus,
      filterByBillable: filter.filterByBillable ?? null,
      filterByDate:
        filter.filterByDate?.[0] === TimePeriod.All ? [] : filter.filterByDate,
      fromDate:
        filter.filterByDate?.[0] === TimePeriod.All
          ? null
          : filter.fromDate?.toISOString(),
      toDate:
        filter.filterByDate?.[0] === TimePeriod.All
          ? null
          : filter.toDate?.toISOString(),
      orgId,
      sortOrder: filter.sortOrder,
      sortBy: filter.sortBy,
      filterByWork: [],
      filterByWorkTask: [],
      pageNumber: page,
      pageSize: TIME_PAGE_SIZE,
    }),
    [customerId, filter, orgId, page],
  );

  const {
    data: filterTime,
    isLoading: isLoadingTime,
    isFetching: isFetchingTime,
    refetch: refetchTime,
  } = useFilterTimeQuery(timeQueryArgs, {
    skip: !orgId || !customerId,
  });

  const {
    data: billableHours,
    isLoading: isLoadingBillableHours,
    isFetching: isFetchingBillableHours,
    refetch: refetchBilledHours,
  } = useGetBilledHoursByCustomerIdQuery(
    { orgId, customerId },
    {
      skip: !orgId || !customerId,
    },
  );

  const getData = useCallback(() => {
    if (!orgId || !customerId) {
      return;
    }

    void refetchTime();
    void refetchBilledHours();
  }, [customerId, orgId, refetchBilledHours, refetchTime]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  useEffect(() => {
    document.title = GetPageTitle("Contact Time");
  }, []);

  return (
    <PageLayout title={""} extraClasses={"overflow-visible"}>
      <TimesListView
        billableHours={billableHours}
        filterTime={filterTime?.items}
        getData={getData}
        filter={filter}
        setFilter={setFilter}
        visibleColumns={CONTACT_TIME_VISIBLE_COLUMNS}
        visibleFilters={CONTACT_TIME_VISIBLE_FILTERS}
        presetFilters={presetFilters}
        isLoading={
          isLoadingTime ||
          isFetchingTime ||
          isLoadingBillableHours ||
          isFetchingBillableHours
        }
        page={filterTime?.pageNumber ?? page}
        totalPages={filterTime?.totalPages ?? 1}
        total={filterTime?.totalCount}
        pageSize={filterTime?.pageSize || TIME_PAGE_SIZE}
        onPageChange={setPage}
      />

      <div>
        <Outlet />
      </div>
    </PageLayout>
  );
};
